import { isEqual } from 'lodash';
import { objectStateComparator, auditLogHeaderEntry } from 'services/auditService';
import { get, post, update, remove } from 'services/ApiClient';
import { endpoint, replaceEndpointPathParams } from 'services/ApiConfig';
import { DATA_NOT_CHANGED_SO_NOT_UPDATED } from 'constants/messages';
import { getBasicAuthorizationHeader } from 'services/projectService';
import Promise from 'promise';
import { isValueEntered } from 'services/Validation';
import { removeEmptyEntries } from 'services/objectService';

export const postDataRequest = async (endpointLookup, body, headers, pathParams, pathParamValues) => {
    const endpointConfig = endpoint(endpointLookup);
    const uri = replaceEndpointPathParams(endpointConfig ?.uri, pathParams, pathParamValues);
    let result = {};
    if (endpointConfig) {
        await post(uri, body, headers)
            .then(res => {
                result = res ? res : {};
            })
            .catch(error => {
                throw error;
            });
    }
    return result;
};

export const getDataRequest = async (endpointLookup) => {
    const endpointConfig = endpoint(endpointLookup);
    let result = {};
    if (endpointConfig) {
        await get(endpointConfig.uri)
            .then(res => {
                result = res ? res : {};
            })
            .catch(error => {
                throw error;
            });
    }
    return result;
};

/**
 * fetch data with or without any path parameters
 * @param {string} endpointLookup - correspond with ApiConfig service endpoints
 * @param {Array} pathParams - array of known pathParams - i.e. ['orgId'] or null if none
 * @param {Array} pathParamValues - array of corresponding pathParamValues or null if none
 */
export const fetchDataRequest = async (endpointLookup, pathParams, pathParamValues) => {
    const endpointConfig = endpoint(endpointLookup);
    const uri = replaceEndpointPathParams(endpointConfig ?.uri, pathParams, pathParamValues);
    return fetchUriDataRequest(uri);
};

export const fetchUriDataRequestWithBasicAuth = (uri, username, password) => {
    const header = getBasicAuthorizationHeader(username, password);
    return fetchUriDataRequest(uri, header, true);
};

/**
 * fetch data using the uri provided
 * @param {string} uri - the uri to use in the get request
 * @param {object} headers - the uri to use in the get request
 * @param {boolean} basicApiCall - the uri to use in the get request
 */
export const fetchUriDataRequest = async (uri, headers, basicApiCall) => {
    let values = [];
    if (uri) {
        await get(uri, headers, basicApiCall || false)
            .then(response => {
                if (Array.isArray(response)) {
                    response.forEach((rawData) => {
                        values.push(rawData);
                    });
                } else {
                    // should be an object so push the single value
                    values = response;
                }
            })
            .catch(error => {
                throw error;
            });
    }
    return values;
};

/**
 * Update the payload object and make the call to the API - return the payload updated (may contain an id if a new object)
 * @param {Array} currentList  - current list that is going to be changed - DEPRECATED PARAMETER (May 2020)
 * @param {Object or payload} payload - the payload being updated/ added
 * @param {string} postEndpoint the add endpoint - key links to ApiConfig
 * @param {string} putEndpoint the update endpoint - key links to ApiConfig
 * @param {Array} pathParams - array of known pathParams - i.e. ['orgId'] or null if none
 * @param {Array} pathParamValues - array of corresponding pathParamValues - i.e. [orgId] or null if none
 */
export const addOrUpdateDataRequest = async (currentList, payload, postEndpoint, putEndpoint, pathParams, pathParamValues) => {
    try {
        let isNew = !isValueEntered(payload?.id);
        let currentState = {};
        payload = removeEmptyEntries(payload);
        let updatedPayload = { ...payload };
        // legacy code - not updated here but in the reducers as lists may be filtered so hard to control here
        // const updatedList = currentList.map((entry) => {
        //     if (entry.id === payload.id) {
        //         isNew = false;
        //         currentState = entry;
        //         return payload;
        //     } else {
        //         return entry;
        //     }
        // });
        // updatedList should now contain either
        // 1. the new list with the updated item
        // 2. The original list without the new item (if adding  an item)
        const endpointConfig = isNew ? endpoint(postEndpoint) : endpoint(putEndpoint);
        const uri = replaceEndpointPathParams(endpointConfig ?.uri, pathParams, pathParamValues);
        if (isNew) {
            if (uri) {
                await post(uri, payload)
                    .then((response) => {
                        // the response will return the id so place it back into the local object
                        const id = response ? response.id : postEndpoint.concat('ResponseError');
                        updatedPayload = { ...payload, id: id };
                        // updatedList.push(updatedPayload);
                    })
                    .catch(error => {
                        throw error;
                    });
            }
        } else {
            if (uri) {
                if (!isEqual(currentState, payload)) {
                    const changedState = objectStateComparator([currentState], [payload]);
                    await update(uri, payload, auditLogHeaderEntry(changedState))
                        .then(payload => payload) // already in list
                        .catch(error => {
                            throw error;
                        });
                } else {
                    throw DATA_NOT_CHANGED_SO_NOT_UPDATED;
                }
            }
        }
        return updatedPayload;
    } catch (error) {
        throw error;
    }
};


/**
 * make a call to the API to update the payload
 * @param {Object or payload} payload - the payload being updated
 * @param {string} putEndpoint the update endpoint - key links to ApiConfig
 * @param {Array} auditMessage - the auditMessage to save with the request
 * @param {Array} pathParams [optional] - array of known pathParams - i.e. ['orgId'] or null if none
 * @param {Array} pathParamValues [optional] - array of corresponding pathParamValues - i.e. [orgId] or null if none
 */
export const updateDataRequest = async (payload, putEndpoint, auditMessage, pathParams, pathParamValues) => {
    try {
        const endpointConfig = endpoint(putEndpoint);
        const uri = replaceEndpointPathParams(endpointConfig ?.uri, pathParams, pathParamValues);
        payload = removeEmptyEntries(payload);
        await update(uri, payload, auditLogHeaderEntry(auditMessage))
            .then(payload => { return payload; })
            .catch(error => {
                throw error;
            });
        return payload;
    } catch (error) {
        throw error;
    }
};

/**
 * make a call to the API to delete the key
 * @param {string} deleteEndpoint the update endpoint - key links to ApiConfig
 * @param {Array} auditMessage - the auditMessage to save with the request
 * @param {Array} pathParams [optional] - array of known pathParams - i.e. ['orgId'] or null if none
 * @param {Array} pathParamValues [optional] - array of corresponding pathParamValues - i.e. [orgId] or null if none
 */
export const deleteDataRequest = async (deleteEndpoint, auditMessage, pathParams, pathParamValues) => {
    try {
        const endpointConfig = endpoint(deleteEndpoint);
        const uri = replaceEndpointPathParams(endpointConfig ?.uri, pathParams, pathParamValues);
        await remove(uri, auditLogHeaderEntry(auditMessage))
            .then(payload => { return payload; })
            .catch(error => {
                throw error;
            });
        return;
    } catch (error) {
        throw error;
    }
};

/**
 * parse error response
 */
export const parseError = (messages) => {
    // error
    if (messages) {
        if (messages instanceof Array) {
            const mappedMessages = messages.map((message => {
                return message ?.error ? message.error : message;
            }));
            return Promise.reject({ messages: [mappedMessages.join()] }); // creates "," separated string of all errors
        } else {
            return Promise.reject({ messages: [messages ?.error ? messages.error : messages] });
        }
    } else {
        return Promise.reject({ messages: ['Error ???'] });
    }
};

/**
 * parse response
 */
export const parseBody = (response) => {
    //  if (response.status === 200 && response.data.status.code === 200) { // - if use custom status code
    if (response ?.status === 200) {
        return Promise.resolve(response.data);
    } else {
        return this.parseError(response.data.messages);
    }
};



// WEBPACK FOOTER //
// ./services/ApiService.js